import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { apiFetch, getApiHost } from '../utils/api';

interface ShillActivityTweet {
  tweetId: string;
  tweetUrl: string;
  tweetText: string;
  authorHandle: string;
  authorFollowers: number;
  engagement: number;
  impressions: number;
  intent: 'promoting' | 'dissuading' | 'neutral';
  createdAt: string;
  recordedAt: string;
  mint: string;
  tokenSymbol: string;
  tokenSafety?: {
    tokenSymbol: string;
    tokenName: string;
    overallSafetyLevel: string;
    topNetworkOwnership: number;
    topNetworkWalletCount: number;
    topNetworkBundlerAddress: string;
    metadata?: { image?: string };
  };
  _score: number;
}

const formatRelativeTime = (dateStr: string): string => {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const formatFollowers = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const INTENT_STYLES = {
  promoting: {
    label: 'Shilling',
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    icon: 'fas fa-thumbs-down',
  },
  dissuading: {
    label: 'Calling Out Scams',
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    icon: 'fas fa-thumbs-up',
  },
  neutral: {
    label: 'Neutral',
    color: 'text-gray-400',
    bg: 'bg-gray-500/10',
    border: 'border-gray-500/30',
    icon: 'fas fa-comment',
  },
} as const;

const SAFETY_COLORS: Record<string, string> = {
  DANGEROUS: 'bg-red-500/20 text-red-400 border-red-500/30',
  RISKY: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  OK: 'bg-green-500/20 text-green-400 border-green-500/30',
  SAFE: 'bg-green-500/20 text-green-400 border-green-500/30',
};

/** Generate pre-filled reply text based on intent */
function buildReplyText(
  intent: 'promoting' | 'dissuading' | 'neutral',
  symbol: string,
  networkPct: number | undefined,
  networkWallets: number | undefined,
  mint: string,
): string {
  const stats = networkPct != null && networkWallets
    ? `${networkPct.toFixed(0)}% of $${symbol} supply is held by ${networkWallets} wallets tracing to one operator.`
    : '';
  const link = `https://deepnets.ai/token/${mint}`;

  if (intent === 'promoting') {
    return `⚠️ Heads up — this token looks risky. ${stats}\n\nSee the full analysis: ${link}`;
  } else if (intent === 'dissuading') {
    return `💯 This checks out. ${stats}\n\nFull breakdown here: ${link}`;
  }
  return `${stats}\n\nAnalysis: ${link}`;
}

type IntentFilter = 'all' | 'promoting' | 'dissuading' | 'neutral';
type TimeFilter = '6' | '24' | '48' | '168';

const VALID_INTENTS: IntentFilter[] = ['all', 'promoting', 'dissuading', 'neutral'];
const VALID_TIMES: TimeFilter[] = ['6', '24', '48', '168'];

const ShillActivityPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialIntent = (VALID_INTENTS.includes(searchParams.get('intent') as IntentFilter)
    ? searchParams.get('intent') as IntentFilter
    : 'promoting');
  const initialTime = (VALID_TIMES.includes(searchParams.get('hours') as TimeFilter)
    ? searchParams.get('hours') as TimeFilter
    : '48');
  const initialAuthor = searchParams.get('author') || '';

  const [tweets, setTweets] = useState<ShillActivityTweet[]>([]);
  const [counts, setCounts] = useState<{ total: number; promoting: number; dissuading: number; neutral: number }>({ total: 0, promoting: 0, dissuading: 0, neutral: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [intentFilter, setIntentFilter] = useState<IntentFilter>(initialIntent);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>(initialTime);
  const [authorSearch, setAuthorSearch] = useState(initialAuthor);

  // Sync filter state to URL params
  const updateUrl = useCallback((intent: IntentFilter, time: TimeFilter, author: string) => {
    const params: Record<string, string> = {};
    if (intent !== 'promoting') params.intent = intent;
    if (time !== '48') params.hours = time;
    if (author.trim()) params.author = author.trim();
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      setError(null);
      const apiHost = getApiHost();
      const params = new URLSearchParams({ hours: timeFilter, limit: '100' });
      if (intentFilter !== 'all') params.set('intent', intentFilter);
      if (authorSearch.trim()) params.set('author', authorSearch.trim());
      const res = await apiFetch(`${apiHost}/api/shill-activity?${params}`);
      if (!res.ok) throw new Error('Failed to fetch activity');
      const data = await res.json();
      setTweets(data.tweets ?? []);
      if (data.counts) setCounts(data.counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, intentFilter]);

  // Debounce author search
  useEffect(() => {
    const timer = setTimeout(() => fetchActivity(), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorSearch]);

  // Filtering is done server-side now
  const filtered = tweets;

  // Group by token for summary stats
  const tokenStats = new Map<string, { count: number; symbol: string }>();
  for (const t of tweets) {
    const existing = tokenStats.get(t.mint);
    if (existing) {
      existing.count++;
    } else {
      tokenStats.set(t.mint, { count: 1, symbol: t.tokenSymbol || t.tokenSafety?.tokenSymbol || '???' });
    }
  }

  const intentCounts = {
    all: counts.total,
    promoting: counts.promoting,
    dissuading: counts.dissuading,
    neutral: counts.neutral,
  };

  return (
    <div className="min-h-screen bg-crypto-dark text-white">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center">
            <i className="fas fa-broadcast-tower text-crypto-accent mr-3"></i>
            Shill Tracker
          </h1>
          <p className="text-gray-400">
            Real-time detection of accounts promoting tokens with concentrated wallet networks on X.
          </p>

        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-crypto-card rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-400">{intentCounts.promoting}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Shilling</div>
          </div>
          <div className="bg-crypto-card rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-400">{intentCounts.dissuading}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Calling Out Scams</div>
          </div>
          <div className="bg-crypto-card rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-white">{tokenStats.size}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Tokens Mentioned</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative">
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs"></i>
            <input
              type="text"
              value={authorSearch}
              onChange={(e) => { setAuthorSearch(e.target.value); updateUrl(intentFilter, timeFilter, e.target.value); }}
              placeholder="Search by handle..."
              className="bg-crypto-gray border border-gray-700 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-crypto-accent transition-colors w-48"
            />
          </div>
          <div className="flex bg-crypto-gray rounded-lg overflow-hidden border border-gray-700">
            {(['all', 'promoting', 'dissuading', 'neutral'] as IntentFilter[]).map(f => (
              <button
                key={f}
                onClick={() => { setIntentFilter(f); updateUrl(f, timeFilter, authorSearch); }}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  intentFilter === f
                    ? 'bg-crypto-accent text-crypto-dark'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {f === 'all' ? 'All' : f === 'promoting' ? 'Shilling' : f === 'dissuading' ? 'Calling Out Scams' : 'Neutral'}
                <span className="ml-1 opacity-60">({intentCounts[f]})</span>
              </button>
            ))}
          </div>

          <div className="flex bg-crypto-gray rounded-lg overflow-hidden border border-gray-700">
            {([['6', '6h'], ['24', '24h'], ['48', '48h'], ['168', '7d']] as [TimeFilter, string][]).map(([val, label]) => (
              <button
                key={val}
                onClick={() => { setTimeFilter(val); updateUrl(intentFilter, val, authorSearch); }}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  timeFilter === val
                    ? 'bg-crypto-accent text-crypto-dark'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-gray-600 border-t-crypto-accent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Loading activity...</p>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="text-center py-16">
            <i className="fas fa-exclamation-triangle text-red-400 text-4xl mb-4"></i>
            <p className="text-gray-400 mb-4">{error}</p>
            <button onClick={fetchActivity} className="bg-crypto-accent text-crypto-dark px-4 py-2 rounded-lg font-medium">
              Retry
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <i className="fas fa-ghost text-4xl mb-4 block"></i>
            No activity found for this filter.
          </div>
        )}

        {/* Activity Feed */}
        {!loading && !error && filtered.length > 0 && (
          <div className="space-y-3">
            {filtered.map((tweet) => {
              const intentStyle = INTENT_STYLES[tweet.intent];
              const safety = tweet.tokenSafety;
              const safetyLevel = safety?.overallSafetyLevel ?? '';
              const safetyColor = SAFETY_COLORS[safetyLevel] ?? 'bg-gray-500/20 text-gray-400 border-gray-500/30';
              const symbol = tweet.tokenSymbol || safety?.tokenSymbol || '???';
              const networkPct = safety?.topNetworkOwnership;
              const networkWallets = safety?.topNetworkWalletCount;

              return (
                <div
                  key={tweet.tweetId}
                  className={`bg-crypto-card rounded-xl p-4 border ${intentStyle.border} hover:border-gray-600 transition-colors`}
                >
                  {/* Top row: intent badge + token pill */}
                  <div className="flex items-start justify-between gap-3 mb-2">
                    {/* Intent badge */}
                    <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${intentStyle.bg} ${intentStyle.color} flex-shrink-0`}>
                      <i className={intentStyle.icon}></i>
                      {intentStyle.label}
                    </span>

                    <div className="flex items-center gap-3 min-w-0">
                      {/* Network info */}
                      {networkPct != null && networkPct > 0 && (
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {networkPct.toFixed(0)}% · {networkWallets} wallets
                        </span>
                      )}

                      {/* Token pill */}
                      <Link
                        to={`/token/${tweet.mint}`}
                        className="flex items-center gap-2 bg-crypto-gray/80 rounded-full px-3 py-1 hover:bg-gray-700 transition-colors flex-shrink-0"
                      >
                        {safety?.metadata?.image ? (
                          <img src={safety.metadata.image} alt="" className="w-5 h-5 rounded-full" />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center text-[10px] text-gray-400">
                            {symbol.charAt(0)}
                          </div>
                        )}
                        <span className="text-sm font-bold text-white">${symbol}</span>
                        {safetyLevel && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${safetyColor}`}>
                            {safetyLevel}
                          </span>
                        )}
                      </Link>
                    </div>
                  </div>

                  {/* Author row */}
                  <div className="flex items-center gap-2 mb-2">
                    <a
                      href={`https://x.com/${tweet.authorHandle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-white hover:text-crypto-accent transition-colors"
                    >
                      @{tweet.authorHandle}
                    </a>
                    <span className="text-xs text-gray-500">{formatFollowers(tweet.authorFollowers)} followers</span>
                    <span className="text-xs text-gray-600">·</span>
                    <span className="text-xs text-gray-500">{formatRelativeTime(tweet.createdAt)}</span>
                    {tweet.engagement > 0 && (
                      <>
                        <span className="text-xs text-gray-600">·</span>
                        <span className="text-xs text-gray-500">
                          <i className="fas fa-heart mr-1"></i>{tweet.engagement}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Tweet text + reply button */}
                  <div className="flex items-start gap-3">
                    <a
                      href={tweet.tweetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm text-gray-300 hover:text-gray-200 transition-colors line-clamp-3 leading-relaxed flex-1"
                    >
                      {tweet.tweetText}
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?in_reply_to=${tweet.tweetId}&text=${encodeURIComponent(buildReplyText(tweet.intent, symbol, networkPct, networkWallets, tweet.mint))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-crypto-gray/80 border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors mt-0.5"
                      title="Reply to this tweet"
                    >
                      <i className="fas fa-reply"></i>
                      Reply
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ShillActivityPage;
